import { CalendarClock, CalendarDays, CalendarPlus } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { AppointmentDrawer, type DrawerMode } from "@/features/appointment-drawer";
import { NewAppointmentDialog } from "@/features/appointment-new";
import { AppointmentRow } from "@/features/opportunity-appointment-row";
import { splitAppointments } from "@/lib/contact-activity";
import { contactName } from "@/lib/contacts";
import { useContactAppointments } from "@/lib/queries";
import type { AppointmentListItem, LeadCard } from "@/lib/types";
import { formatPhoneNumber } from "@/lib/utils";

/**
 * Book or update an appointment on the opportunity, without leaving it (ACL-411).
 *
 * **Nothing here schedules anything itself.** Booking is `NewAppointmentDialog` with the
 * opportunity's contact fixed, and changing one is `AppointmentDrawer` — the same two surfaces the
 * Calendars page and a conversation use, with the same calendars, the same free times and the same
 * rules about who may host. A second scheduling flow is the thing the ticket asks us not to build:
 * two of them drift, and the one that drifts is the one nobody is looking at.
 *
 * So this file is a list and two buttons. The reason it exists at all is that the list has to say
 * which appointment to change, and the opportunity has to say who to book — neither of which the
 * generic surfaces can know.
 *
 * **Why the drawer opens over the dialog rather than navigating.** The point of the ticket is not
 * leaving the opportunity. The opportunity dialog already nests `DeleteLeadDialog` and
 * `ContactOutcomeDialog` this way, so the pattern is the file's own. The "Open in Calendars" link
 * stays on each row for the person who does want the full page.
 *
 * `source` is `manual`: the appointment's provenance vocabulary is a closed set on the API
 * (`manual | conversation | api`) and a booking made here is one a person made in the console. A
 * distinct `opportunity` source would be truer, and is a backend change — worth doing the day
 * reporting needs to tell the two apart, not before.
 */
export function OpportunityAppointments({ contact }: { contact: LeadCard }) {
  const { data, isPending, error } = useContactAppointments(contact.id);
  const [booking, setBooking] = React.useState(false);
  // Which appointment the drawer is on, and whether it opened to read or straight to a new time.
  const [open, setOpen] = React.useState<{ id: number; mode: DrawerMode } | null>(null);

  const who = contactName(contact) ?? contact.phone;
  const phone = formatPhoneNumber(contact.phone);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-medium text-text">Appointments</h3>
          <p className="mt-0.5 text-xs text-text-muted">
            Booked with {who} — on the same calendars as the Calendars page.
          </p>
        </div>
        <Button size="sm" onClick={() => setBooking(true)}>
          <CalendarPlus />
          Book appointment
        </Button>
      </div>

      <Body
        list={data}
        isPending={isPending}
        error={error}
        who={who}
        phone={phone}
        onOpen={(id, mode) => setOpen({ id, mode })}
      />

      {/* Mounted only while open: both carry their own queries, and an opportunity dialog that
          opens on Notes should not fetch a calendar's free times. */}
      {booking && (
        <NewAppointmentDialog
          open
          onOpenChange={setBooking}
          contact={{ id: contact.id, name: who, phone: contact.phone, timezone: contact.clock_timezone ?? contact.timezone }}
          source="manual"
        />
      )}
      {open && (
        <AppointmentDrawer
          appointmentId={open.id}
          startIn={open.mode}
          onOpenChange={(isOpen) => !isOpen && setOpen(null)}
          // A reschedule books a new appointment and closes the old one; follow it, or the drawer
          // would sit on a row that now reads "rescheduled" and points elsewhere.
          onOpenAppointment={(id) => setOpen({ id, mode: "view" })}
        />
      )}
    </div>
  );
}

function Body({
  list, isPending, error, who, phone, onOpen,
}: {
  list: AppointmentListItem[] | undefined;
  isPending: boolean;
  error: unknown;
  who: string;
  phone: string;
  onOpen: (id: number, mode: DrawerMode) => void;
}) {
  if (isPending) {
    return (
      <div className="flex justify-center py-8">
        <Spinner className="size-4" />
      </div>
    );
  }
  if (error) return <ErrorState error={error} title="Could not load the appointments" />;
  if (!list || list.length === 0) {
    return (
      <EmptyState
        icon={CalendarClock}
        title="Nothing booked yet"
        description={`Book a callback or a consult with ${who} and it will show up here, with the ones that have already happened.`}
      />
    );
  }

  const { upcoming, past } = splitAppointments(list, Date.now());
  return (
    <div className="space-y-5">
      <Group title="Upcoming" list={upcoming} phone={phone} empty="Nothing booked ahead." onOpen={onOpen} />
      <Group title="Past" list={past} phone={phone} empty="No past appointments." onOpen={onOpen} />
    </div>
  );
}

function Group({
  title, list, phone, empty, onOpen,
}: {
  title: string;
  list: AppointmentListItem[];
  phone: string;
  empty: string;
  onOpen: (id: number, mode: DrawerMode) => void;
}) {
  const id = React.useId();
  return (
    <section aria-labelledby={id} className="space-y-2">
      <h4 id={id} className="text-xs font-semibold uppercase tracking-wide text-text-subtle">
        {title} <span className="font-normal tabular-nums">({list.length})</span>
      </h4>
      {list.length === 0 ? (
        <p className="flex items-center gap-1.5 text-xs text-text-muted">
          <CalendarDays className="size-3.5" aria-hidden />
          {empty}
        </p>
      ) : (
        <ol className="space-y-2">
          {list.map((a) => (
            <li key={a.id}>
              <AppointmentRow appointment={a} phone={phone} onOpen={onOpen} />
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
